/**
 * F42 — comprehensive admin venue editor.
 *
 * The super-admin "edit everything" form for a venue's core fields. Writes
 * through `PATCH /admin/venues/:id/fields` (the F42 `editFields` endpoint),
 * which is If-Match-guarded on `venues.updated_at`, audited to
 * `venue_admin_audit`, and supports an optional per-field lock against owner
 * (F12) edits. Only CHANGED fields are sent.
 *
 * Covers: name, phone, WhatsApp, description, street address, location
 * (draggable map → lat/lng), social links, and the bookable toggle. A field
 * the admin has locked shows a badge + Unlock and is disabled until unlocked.
 *
 * NOT covered here (deliberately): opening hours (needs a day-by-day editor),
 * region (governorate/caza/city — carries duplicate-detection implications),
 * photos (separate upload/moderation surface), and sports/courts (its own tab).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  OsmMapPicker,
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  FormField,
  Switch,
  Textarea,
  TextInput,
  useToast,
} from '@bravemove/ui-web';
import type { OsmMapBbox } from '@bravemove/ui-web';
import type { AdminVenueDetail, AdminVenueProfile, NameLockOverride } from '@bravemove/types';
import { adminVenuesApi } from '../api/adminVenues';
import { translateApiError } from '../auth/errors.js';
import { useVerificationPermissions } from '../hooks/useVerificationPermissions';
import { NameLockOverridePanel } from './verification/NameLockOverride';
import { isNameLockedError } from './verification/verificationErrors';

/** Lebanon-wide clamp for the draggable location pin. */
const LEBANON_BBOX: OsmMapBbox = { minLat: 33.05, maxLat: 34.7, minLng: 35.1, maxLng: 36.7 };

const SOCIAL_KEYS = ['instagram', 'facebook', 'tiktok', 'website'] as const;

function trimOrNull(s: string): string | null {
  const v = s.trim();
  return v.length === 0 ? null : v;
}

type TranslateFn = ReturnType<typeof useI18n>['t'];

interface VenueEditFormProps {
  readonly detail: AdminVenueDetail;
  readonly profile: AdminVenueProfile | null;
  readonly canEdit: boolean;
  /** Updated detail after a successful save (or unlock). */
  readonly onSaved: (next: AdminVenueDetail) => void;
  /** Re-fetch the rich profile (the edited scalar/jsonb fields live there). */
  readonly onProfileRefetch: () => void;
  /** Full reload of detail + profile (used after a stale-conflict). */
  readonly onReload: () => void;
}

export function VenueEditForm({
  detail,
  profile,
  canEdit,
  onSaved,
  onProfileRefetch,
  onReload,
}: VenueEditFormProps) {
  const { t } = useI18n();
  const toast = useToast();

  // Initial snapshot for change-detection. Keyed on detail.updatedAt at the call
  // site so the form remounts (re-initialises) after every successful save.
  const initial = useMemo(
    () => ({
      name: detail.name,
      phone: profile?.phone ?? '',
      whatsappPhone: profile?.whatsappPhone ?? '',
      description: profile?.description ?? '',
      streetAddress: profile?.streetAddress ?? '',
      lat: profile?.lat ?? '',
      lng: profile?.lng ?? '',
      bookable: profile?.bookable ?? false,
      social: (profile?.socialLinks ?? {}) as Record<string, string>,
    }),
    [detail, profile],
  );

  const [name, setName] = useState(initial.name);
  const [phone, setPhone] = useState(initial.phone);
  const [whatsappPhone, setWhatsappPhone] = useState(initial.whatsappPhone);
  const [description, setDescription] = useState(initial.description);
  const [streetAddress, setStreetAddress] = useState(initial.streetAddress);
  const [lat, setLat] = useState(initial.lat);
  const [lng, setLng] = useState(initial.lng);
  const [bookable, setBookable] = useState(initial.bookable);
  const [social, setSocial] = useState<Record<string, string>>(() => ({
    instagram: initial.social.instagram ?? '',
    facebook: initial.social.facebook ?? '',
    tiktok: initial.social.tiktok ?? '',
    website: initial.social.website ?? '',
  }));

  const [lockChanged, setLockChanged] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  // F09 v2 — the last save hit a verified venue's name (422 NAME_LOCKED_VERIFIED).
  const [nameLockError, setNameLockError] = useState<unknown>(null);
  const { canReview: canOverrideNameLock } = useVerificationPermissions();

  const lockOf = (field: string) =>
    detail.adminLockedFields.find((l) => l.fieldPath === field) ?? null;

  const latN = Number(lat);
  const lngN = Number(lng);
  const hasGeo = lat !== '' && lng !== '' && Number.isFinite(latN) && Number.isFinite(lngN);

  /**
   * Pure change-collector (no side effects) — used both to gate the Save button
   * and to build the payload. `geoInvalid` is true when lat/lng were edited to a
   * non-numeric value, so Save stays enabled to surface the error on click.
   */
  function collectChanges(): { fields: Record<string, unknown>; geoInvalid: boolean } {
    const fields: Record<string, unknown> = {};
    let geoInvalid = false;
    if (name !== initial.name) fields.name = name.trim();
    if (phone !== initial.phone) fields.phone = phone.trim();
    if (whatsappPhone !== initial.whatsappPhone) fields.whatsappPhone = trimOrNull(whatsappPhone);
    if (description !== initial.description) fields.description = trimOrNull(description);
    if (streetAddress !== initial.streetAddress) fields.streetAddress = trimOrNull(streetAddress);

    if (lat !== initial.lat || lng !== initial.lng) {
      if (hasGeo) {
        fields.lat = Number(latN.toFixed(6));
        fields.lng = Number(lngN.toFixed(6));
      } else {
        geoInvalid = true;
      }
    }

    const curSocial: Record<string, string> = {};
    for (const k of SOCIAL_KEYS) {
      const v = (social[k] ?? '').trim();
      if (v) curSocial[k] = v;
    }
    if (JSON.stringify(curSocial) !== JSON.stringify(initial.social)) {
      fields.socialLinks = Object.keys(curSocial).length > 0 ? curSocial : null;
    }

    if (bookable !== initial.bookable) fields.bookable = bookable;
    return { fields, geoInvalid };
  }

  const handleSave = async (nameLockOverride?: NameLockOverride) => {
    const { fields, geoInvalid } = collectChanges();
    if (geoInvalid) {
      toast.push({
        tone: 'danger',
        title: t('admin:venue.f42.edit.badGeo', {
          defaultValue: 'Latitude and longitude must both be valid numbers.',
        }),
      });
      return;
    }
    if (Object.keys(fields).length === 0) {
      toast.push({
        tone: 'info',
        title: t('admin:venue.f42.edit.noChanges', { defaultValue: 'No changes to save.' }),
      });
      return;
    }
    setSaving(true);
    try {
      const next = await adminVenuesApi.editFields(
        detail.id,
        {
          fields,
          ...(lockChanged ? { lockFields: Object.keys(fields) } : {}),
          ...(lockChanged && lockReason.trim() ? { reason: lockReason.trim() } : {}),
          ...(nameLockOverride ? { nameLockOverride } : {}),
        },
        detail.updatedAt,
      );
      setNameLockError(null);
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.profile.saved', { defaultValue: 'Venue updated.' }),
      });
      onSaved(next);
      onProfileRefetch();
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'body' in err
          ? (err as { body?: { error?: { code?: string } } }).body?.error?.code
          : undefined;
      if (code === 'STALE_VENUE') setStaleOpen(true);
      else if (isNameLockedError(err)) setNameLockError(err);
      else toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (field: string) => {
    try {
      const next = await adminVenuesApi.unlockFields(detail.id, { fields: [field] });
      toast.push({
        tone: 'success',
        title: t('admin:venue.f42.profile.unlocked', { defaultValue: 'Field unlocked.' }),
      });
      onSaved(next);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  if (!canEdit) {
    return (
      <Banner tone="info">
        {t('admin:venue.f42.detail.noPermission', {
          defaultValue: 'You do not have permission to edit this venue.',
        })}
      </Banner>
    );
  }

  const pending = collectChanges();
  const dirty = Object.keys(pending.fields).length > 0 || pending.geoInvalid;

  return (
    <div className="space-y-3" data-testid="admin-venue-edit-form">
      <Section title={t('admin:venue.f42.edit.basics', { defaultValue: 'Basics' })}>
        <div className="grid gap-3 sm:grid-cols-2">
          <LockableField
            field="name"
            label={t('admin:venue.fields.name', { defaultValue: 'Name' })}
            lock={lockOf('name')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                // A new name needs a new lock decision.
                setNameLockError(null);
              }}
              disabled={lockOf('name') !== null}
              data-testid="admin-venue-edit-name"
            />
          </LockableField>
          <LockableField
            field="phone"
            label={t('admin:venue.fields.phone', { defaultValue: 'Phone' })}
            lock={lockOf('phone')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={lockOf('phone') !== null}
              placeholder="+961…"
              data-testid="admin-venue-edit-phone"
            />
          </LockableField>
          <LockableField
            field="whatsappPhone"
            label={t('admin:venue.f42.overview.whatsapp', { defaultValue: 'WhatsApp' })}
            lock={lockOf('whatsappPhone')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={whatsappPhone}
              onChange={(e) => setWhatsappPhone(e.target.value)}
              disabled={lockOf('whatsappPhone') !== null}
              placeholder="+961…"
              data-testid="admin-venue-edit-whatsapp"
            />
          </LockableField>
          <LockableField
            field="streetAddress"
            label={t('admin:venue.f42.edit.streetAddress', { defaultValue: 'Street address' })}
            lock={lockOf('streetAddress')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              disabled={lockOf('streetAddress') !== null}
              data-testid="admin-venue-edit-street"
            />
          </LockableField>
        </div>
        <div className="mt-3">
          <LockableField
            field="description"
            label={t('admin:venue.f42.edit.description', { defaultValue: 'Description' })}
            lock={lockOf('description')}
            onUnlock={handleUnlock}
            t={t}
          >
            <Textarea
              rows={3}
              maxLength={500}
              showCounter
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={lockOf('description') !== null}
              data-testid="admin-venue-edit-description"
            />
          </LockableField>
        </div>
      </Section>

      <Section title={t('admin:venue.f42.overview.location', { defaultValue: 'Location' })}>
        <p className="mb-2 text-[12px] text-ink-3">
          {t('admin:venue.f42.edit.locationHint', {
            defaultValue: 'Drag the pin to move the venue, or type coordinates below.',
          })}
        </p>
        {hasGeo ? (
          <div className="overflow-hidden rounded-lg border border-hairline">
            <OsmMapPicker
              lat={latN}
              lng={lngN}
              editable={lockOf('lat') === null && lockOf('lng') === null}
              bbox={LEBANON_BBOX}
              height={260}
              ariaLabel={t('admin:venue.f42.overview.locationMap', {
                defaultValue: 'Venue location',
              })}
              onChange={({ lat: la, lng: ln }) => {
                setLat(String(la));
                setLng(String(ln));
              }}
            />
          </div>
        ) : (
          <Banner tone="info">
            {t('admin:venue.f42.edit.noGeoYet', {
              defaultValue: 'No coordinates yet — enter a latitude and longitude below.',
            })}
          </Banner>
        )}
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <LockableField
            field="lat"
            label={t('admin:venue.f42.edit.lat', { defaultValue: 'Latitude' })}
            lock={lockOf('lat')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              disabled={lockOf('lat') !== null}
              data-testid="admin-venue-edit-lat"
            />
          </LockableField>
          <LockableField
            field="lng"
            label={t('admin:venue.f42.edit.lng', { defaultValue: 'Longitude' })}
            lock={lockOf('lng')}
            onUnlock={handleUnlock}
            t={t}
          >
            <TextInput
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              disabled={lockOf('lng') !== null}
              data-testid="admin-venue-edit-lng"
            />
          </LockableField>
        </div>
      </Section>

      {/* V-15 — a `socialLinks` lock is ONE lock covering all four inputs:
          disable them + show a single badge/unlock row. */}
      <Section title={t('admin:venue.f42.overview.socials', { defaultValue: 'Social links' })}>
        <div className="grid gap-3 sm:grid-cols-2">
          {SOCIAL_KEYS.map((k) => (
            <FormField key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}>
              <TextInput
                value={social[k] ?? ''}
                onChange={(e) => setSocial((p) => ({ ...p, [k]: e.target.value }))}
                placeholder={k === 'website' ? 'https://…' : `@handle`}
                disabled={lockOf('socialLinks') !== null}
                data-testid={`admin-venue-edit-social-${k}`}
              />
            </FormField>
          ))}
        </div>
        <LockBadgeRow
          field="socialLinks"
          lock={lockOf('socialLinks')}
          onUnlock={handleUnlock}
          t={t}
        />
      </Section>

      <Section title={t('admin:venue.f42.overview.operations', { defaultValue: 'Operations' })}>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-outline bg-surface-2 px-3 py-2">
          <div>
            <span className="text-[12.5px] font-medium text-ink">
              {t('admin:venue.f42.overview.bookable', { defaultValue: 'Bookable' })}
            </span>
            <p className="text-[11px] text-ink-3">
              {t('admin:venue.f42.edit.bookableHint', {
                defaultValue: 'Allow players to request bookings at this venue.',
              })}
            </p>
          </div>
          <Switch
            checked={bookable}
            onChange={(e) => setBookable(e.target.checked)}
            disabled={lockOf('bookable') !== null}
            data-testid="admin-venue-edit-bookable"
          />
        </div>
        <LockBadgeRow field="bookable" lock={lockOf('bookable')} onUnlock={handleUnlock} t={t} />
      </Section>

      {nameLockError !== null ? (
        <NameLockOverridePanel
          error={nameLockError}
          canOverride={canOverrideNameLock}
          busy={saving}
          onOverride={(override) => void handleSave(override)}
          onDismiss={() => setNameLockError(null)}
          testId="admin-venue-edit-name-lock"
        />
      ) : null}

      {/* Save bar: lock-on-save option + the action. */}
      <div className="flex flex-col gap-3 border-t border-hairline pt-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <label className="flex items-center gap-2 text-[12px] text-ink-2">
            <input
              type="checkbox"
              checked={lockChanged}
              onChange={(e) => setLockChanged(e.target.checked)}
              data-testid="admin-venue-edit-lock-toggle"
            />
            {t('admin:venue.f42.edit.lockOnSave', {
              defaultValue: 'Lock the fields I change (prevents the venue owner from editing them)',
            })}
          </label>
          {lockChanged ? (
            <div className="mt-2 max-w-sm">
              <TextInput
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                placeholder={t('admin:venue.f42.profile.lockReason', {
                  defaultValue: 'Reason (optional)',
                })}
                data-testid="admin-venue-edit-lock-reason"
              />
            </div>
          ) : null}
        </div>
        <Button
          variant="primary"
          loading={saving}
          disabled={!dirty}
          onClick={() => void handleSave()}
          data-testid="admin-venue-edit-save"
        >
          {t('admin:venue.f42.profile.save', { defaultValue: 'Save changes' })}
        </Button>
      </div>

      <Dialog
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        onConfirm={() => {
          setStaleOpen(false);
          onReload();
        }}
        title={t('admin:venue.f42.stale.title', { defaultValue: 'Venue changed' })}
        confirmLabel={t('admin:venue.f42.stale.reload', { defaultValue: 'Reload' })}
        cancelLabel={t('admin:common.cancel', { defaultValue: 'Cancel' })}
      >
        <p className="text-[13px] text-ink-2" data-testid="admin-venue-edit-stale">
          {t('admin:venue.f42.stale.body', {
            defaultValue:
              'This venue was modified since you opened it. Reload to get the latest before editing.',
          })}
        </p>
      </Dialog>
    </div>
  );
}

// ─── Local presentational helpers ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <h3 className="mb-1.5 text-[13px] font-semibold text-ink">{title}</h3>
      {children}
    </Card>
  );
}

/** The lock badge + unlock affordance shared by LockableField and the
 *  section-level locks (socialLinks / bookable — V-15). */
function LockBadgeRow({
  field,
  lock,
  onUnlock,
  t,
}: {
  field: string;
  lock: { readonly reason: string | null } | null;
  onUnlock: (field: string) => void;
  t: TranslateFn;
}) {
  if (lock === null) return null;
  return (
    <p className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
      <Badge tone="warning" size="sm" data-testid={`admin-venue-lock-badge-${field}`}>
        {t('admin:venue.f42.badge.locked', { defaultValue: 'Locked' })}
      </Badge>
      <span>{lock.reason ?? t('admin:venue.f42.profile.lockedHint', { defaultValue: '' })}</span>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => onUnlock(field)}
        data-testid={`admin-venue-unlock-${field}`}
      >
        {t('admin:venue.f42.profile.unlock', { defaultValue: 'Unlock' })}
      </Button>
    </p>
  );
}

function LockableField({
  field,
  label,
  lock,
  onUnlock,
  t,
  children,
}: {
  field: string;
  label: string;
  lock: { readonly reason: string | null } | null;
  onUnlock: (field: string) => void;
  t: TranslateFn;
  children: ReactNode;
}) {
  return (
    <FormField label={label}>
      {children}
      <LockBadgeRow field={field} lock={lock} onUnlock={onUnlock} t={t} />
    </FormField>
  );
}
